import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, Edit3, Trash2, RefreshCw, Sparkles } from "lucide-react";
import toast from "react-hot-toast";
import {
  card,
  microLabel,
  sectionTitle,
  accentBarSection,
  bodySecondary,
  motionStaggerChild,
  emptyState,
  iconSize,
} from "../../../../../lib/design-system";
import { cn } from "../../../../../lib/utils";
import {
  listPSOs,
  createPSO,
  updatePSO,
  deletePSO,
} from "../../../../../services/program.service";
import OutcomeForm from "../OutcomeForm";

const PSOsTab = ({ programId }) => {
  const [psos, setPsos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchPSOs = async () => {
    try {
      setLoading(true);
      const data = await listPSOs(programId);
      setPsos(data.psos || []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to load PSOs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPSOs();
  }, [programId]);

  const handleDelete = async (pso) => {
    if (!window.confirm(`Delete ${pso.code}?`)) return;
    try {
      await deletePSO(programId, pso._id);
      toast.success(`${pso.code} deleted`);
      fetchPSOs();
    } catch (err) {
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  const handleFormSubmit = async (payload) => {
    if (editing) {
      await updatePSO(programId, editing._id, payload);
    } else {
      await createPSO(programId, payload);
    }
    fetchPSOs();
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className={cn(sectionTitle, "text-xl sm:text-2xl")}>
            Program Specific Outcomes
          </h2>
          <div className={cn(accentBarSection, "mt-2")} />
          <p className={cn(bodySecondary, "mt-3 max-w-2xl")}>
            Department-specific outcomes (typically 2 – 4 per program) defining
            domain expertise beyond the standard 12 POs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchPSOs}
            className="px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Refresh"
          >
            <RefreshCw className={iconSize.md} />
          </button>
          <button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            className="inline-flex items-center gap-2 bg-accent1 text-white font-semibold px-4 py-2 rounded-sm hover:opacity-90 transition-opacity"
          >
            <Plus className={iconSize.md} />
            Add PSO
          </button>
        </div>
      </div>

      {loading ? (
        <SkeletonList />
      ) : psos.length === 0 ? (
        <div className={emptyState}>
          <div className="mx-auto h-14 w-14 rounded-sm bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
            <Sparkles className="h-7 w-7 text-gray-400" />
          </div>
          <div className="font-bold text-gray-700 dark:text-gray-200">
            No PSOs defined yet
          </div>
          <div className={cn(bodySecondary, "mt-1")}>
            Add 2 – 4 program-specific outcomes for your department.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {psos.map((pso, i) => (
            <motion.div
              key={pso._id}
              {...motionStaggerChild(i, 0)}
              className={cn(card, "p-5 group")}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="h-1 w-8 bg-accent1 rounded-sm mb-2" />
                  <div className={microLabel}>{pso.code}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">
                    {pso.statement}
                  </p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                  <button
                    onClick={() => {
                      setEditing(pso);
                      setShowForm(true);
                    }}
                    className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    title="Edit"
                    aria-label="Edit"
                  >
                    <Edit3 className={iconSize.sm} />
                  </button>
                  <button
                    onClick={() => handleDelete(pso)}
                    className="p-2 rounded-sm hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600"
                    title="Delete"
                    aria-label="Delete"
                  >
                    <Trash2 className={iconSize.sm} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {showForm && (
        <OutcomeForm
          type="PSO"
          outcome={editing}
          onSubmit={handleFormSubmit}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

const SkeletonList = () => (
  <div className="space-y-3">
    {[0, 1, 2].map((i) => (
      <div key={i} className={cn(card, "p-5")}>
        <div className="flex gap-4">
          <div className="space-y-2">
            <div className="h-1 w-8 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
            <div className="h-3 w-10 bg-gray-200 dark:bg-gray-700 rounded-sm animate-pulse" />
          </div>
          <div className="flex-1 space-y-2">
            <div className="h-3 w-full bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
            <div className="h-3 w-3/4 bg-gray-100 dark:bg-gray-800 rounded-sm animate-pulse" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default PSOsTab;
